import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { Leaf, ChevronRight, Star, ArrowRight, Sparkles, Upload, Wand2, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/button";
import WishlistButton from "../components/WishlistButton";
import Navbar from "../components/Navbar";


const LandingPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories] = useState([
    { id: "furniture", name: "Furniture", icon: "🪑" },
    { id: "interior-decor", name: "Interior Decor", icon: "🏠" },
    { id: "eco-sustainable", name: "Eco Sustainable", icon: "🌿" },
    { id: "handmade-crafts", name: "Handmade Crafts", icon: "🎨" },
    { id: "artwork", name: "Artwork", icon: "🖼️" },
    { id: "finishing-materials", name: "Finishing Materials", icon: "🧱" },
    { id: "building-materials", name: "Building Materials", icon: "🏗️" }
  ]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?limit=8`);
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products");
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar showFullNav />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1679862342541-e408d4f3ab80?w=1920&q=80" 
            alt="Luxury interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-2xl">
            <span className="text-sm font-bold uppercase tracking-[0.25em] text-[#FAB05B] animate-fade-in">Premium Interior Design Marketplace</span>
            <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light text-white mt-4 leading-tight animate-slide-up">
              Transform Your Space with AI-Powered Design
            </h1>
            <p className="text-white/80 text-lg mt-6 font-light leading-relaxed animate-slide-up stagger-2">
              Discover curated furniture, decor, and sustainable materials. Visualise products in your room with our AI before you buy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 animate-slide-up stagger-3">
              <Button 
                onClick={() => navigate("/products")}
                className="bg-white text-[#1A1B1A] hover:bg-[#F7F5F0] rounded-none px-10 py-6 text-base font-medium"
                data-testid="shop-now-btn"
              >
                Shop Now
              </Button>
              <Button 
                onClick={() => navigate("/ai-visualizer")}
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-none px-10 py-6 text-base font-medium"
                data-testid="try-ai-btn"
              >
                <Sparkles size={18} className="mr-2" /> Try AI Visualiser
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* AI Visualiser Showcase */}
      <section className="py-24 bg-white" data-testid="ai-showcase-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="category-badge">AI-Powered Design</span>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mt-4">
                See It Before You Buy It
              </h2>
              <p className="text-[#5C5D5C] mt-6 font-light leading-relaxed">
                Our AI Room Visualiser lets you place products in your space virtually. Generate stunning room designs, get instant shopping lists, and purchase with confidence.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center gap-3 text-[#5C5D5C]">
                  <span className="w-8 h-8 rounded-full bg-[#20C997]/10 flex items-center justify-center text-[#20C997]">✓</span>
                  <span className="font-light">4 free AI room designs for all users</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C5D5C]">
                  <span className="w-8 h-8 rounded-full bg-[#20C997]/10 flex items-center justify-center text-[#20C997]">✓</span>
                  <span className="font-light">Instant shopping list generation</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C5D5C]">
                  <span className="w-8 h-8 rounded-full bg-[#20C997]/10 flex items-center justify-center text-[#20C997]">✓</span>
                  <span className="font-light">One-click purchase from visualisation</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate("/ai-visualizer")}
                className="mt-8 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none px-8 py-4"
                data-testid="visualize-room-btn"
              >
                Visualise Your Room <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1742415106160-594d07f6cc23?w=600&q=80" 
                  alt="Before - Blueprint"
                  className="rounded-lg shadow-lg"
                />
                <img 
                  src="https://images.unsplash.com/photo-1772475329864-e30a2f1278c0?w=600&q=80" 
                  alt="After - Furnished"
                  className="rounded-lg shadow-lg mt-8"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#F06570] text-white px-6 py-3 rounded-lg font-medium">
                AI Generated
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-[#F7F5F0]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="category-badge">Explore</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mt-4">
              Shop by Category
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {categories.map((cat, index) => (
              <button
                key={cat.id}
                onClick={() => navigate(`/products?category=${cat.id}`)}
                className="bg-white p-6 text-center hover:shadow-lg transition-all duration-300 group"
                data-testid={`category-${cat.id}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="text-3xl block mb-3">{cat.icon}</span>
                <span className="text-sm font-medium text-[#1A1B1A] group-hover:text-[#20C997] transition-colors">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-white" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="category-badge">Curated Selection</span>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mt-4">
                Featured Products
              </h2>
            </div>
            <Button 
              onClick={() => navigate("/products")}
              variant="ghost"
              className="text-[#20C997] hover:text-[#1AAE82]"
            >
              View All <ChevronRight size={18} />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="product-card bg-white overflow-hidden cursor-pointer relative"
                onClick={() => navigate(`/products/${product.id}`)}
                data-testid={`product-card-${index}`}
              >
                <WishlistButton productId={product.id} className="absolute top-3 right-3 z-10" />
                <div className="aspect-square overflow-hidden bg-[#EFECE5]">
                  <img 
                    src={product.images?.[0] || "https://via.placeholder.com/400"} 
                    alt={product.name}
                    className="w-full h-full object-cover product-image"
                  />
                </div>
                <div className="p-4">
                  {product.eco_sustainable && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#3E6656] bg-[#3E6656]/10 px-2 py-1 rounded mb-2">
                      <Leaf size={12} /> Eco
                    </span>
                  )}
                  <h3 className="font-medium text-[#1A1B1A] mt-1">{product.name}</h3>
                  <p className="text-sm text-[#5C5D5C] mt-1 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-lg font-semibold text-[#1A1B1A]">£{product.price}</span>
                    {product.rating > 0 && (
                      <span className="flex items-center gap-1 text-sm text-[#5C5D5C]">
                        <Star size={14} className="fill-[#D4A373] text-[#D4A373]" />
                        {product.rating}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Homeowners Section */}
      <section className="py-24 bg-white" data-testid="homeowner-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#F06570]">For Homeowners</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mt-4">
              Design Your Dream Space
            </h2>
            <p className="text-[#5C5D5C] mt-4 max-w-2xl mx-auto font-light text-base md:text-lg">
              Upload your floor plan, customise every detail, and shop real furniture — all in three simple steps.
            </p>
            <p className="mt-6 text-xl md:text-2xl font-semibold text-[#1A1B1A]">
              Get your first <span className="text-[#20C997]">4 room designs</span> for free
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 mb-16">
            {/* Step 1: Upload */}
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full bg-[#20C997]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#20C997]/20 transition-colors">
                <Upload className="text-[#20C997]" size={32} />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#20C997]">Step 1</span>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mt-2">Upload</h3>
              <p className="text-[#5C5D5C] mt-3 font-light leading-relaxed">
                Describe your room or share your 2D floor plan. Our Spatial Design Engine analyses dimensions, lighting, and layout.
              </p>
            </div>

            {/* Step 2: Customise */}
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full bg-[#F06570]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#F06570]/20 transition-colors">
                <Wand2 className="text-[#F06570]" size={32} />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F06570]">Step 2</span>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mt-2">Customise</h3>
              <p className="text-[#5C5D5C] mt-3 font-light leading-relaxed">
                Choose your style, set honest colours and materials. Our Style Consistency engine ensures every room in your property matches perfectly.
              </p>
            </div>

            {/* Step 3: Shop */}
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full bg-[#FAB05B]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#FAB05B]/20 transition-colors">
                <ShoppingBag className="text-[#FAB05B]" size={32} />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FAB05B]">Step 3</span>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mt-2">Shop</h3>
              <p className="text-[#5C5D5C] mt-3 font-light leading-relaxed">
                Browse your customised shopping list and purchase the entire look from our curated marketplace of premium vendors — all in one click.
              </p>
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate("/auth")}
              className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none px-12 py-6 text-lg font-medium"
              data-testid="start-free-visualisation-btn"
            >
              <Sparkles size={22} className="mr-3" /> Start Your Free Visualisation
            </Button>
            <p className="text-sm text-[#8E8F8E] mt-4">No credit card required for your first 4 designs</p>
          </div>
        </div>
      </section>

      {/* Vendor CTA */}
      <section className="py-24 bg-[#20C997]" data-testid="vendor-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-white">
            Become a Vendor
          </h2>
          <p className="text-white/80 mt-6 max-w-2xl mx-auto font-light">
            Join our marketplace and reach thousands of interior design enthusiasts. Competitive commission rates starting at 15% for high-volume sellers.
          </p>
          <Button 
            onClick={() => navigate("/auth?role=vendor")}
            className="mt-10 bg-white text-[#20C997] hover:bg-[#F7F5F0] rounded-none px-10 py-4 text-base font-medium"
            data-testid="become-vendor-btn"
          >
            Start Selling Today
          </Button>
        </div>
      </section>

      {/* Designer Subscription CTA */}
      <section className="py-24 bg-[#EFECE5]" data-testid="subscription-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="category-badge">For Interior Designers</span>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mt-4">
                Unlimited AI Tools
              </h2>
              <p className="text-[#5C5D5C] mt-6 font-light leading-relaxed">
                Subscribe to unlock unlimited AI room visualisations, priority design consultations, and exclusive product recommendations for your clients.
              </p>
              <div className="flex gap-8 mt-8">
                <div className="text-center">
                  <span className="text-3xl font-light text-[#1A1B1A]">£24.99</span>
                  <span className="block text-sm text-[#5C5D5C] mt-1">Monthly</span>
                </div>
                <div className="text-center">
                  <span className="text-3xl font-light text-[#1A1B1A]">£249.99</span>
                  <span className="block text-sm text-[#5C5D5C] mt-1">Annual (save 17%)</span>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/subscription")}
                className="mt-8 bg-[#F06570] hover:bg-[#D9525D] text-white rounded-none px-8 py-4"
                data-testid="subscribe-btn"
              >
                Subscribe Now
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760072513442-9872656c1b07?w=800&q=80" 
                alt="Designer workspace"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1B1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <span className="font-['Cormorant_Garamond'] text-2xl font-semibold">
                <img src="/logo.png" alt="ARKY World" className="h-10 w-auto" />
              </span>
              <p className="text-white/60 mt-4 font-light text-sm">
                Premium interior design marketplace with AI-powered visualisation tools. Visit us at www.arky.world
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Shop</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><Link to="/products" className="hover:text-white transition-colors">All Products</Link></li>
                <li><Link to="/products?category=furniture" className="hover:text-white transition-colors">Furniture</Link></li>
                <li><Link to="/products?eco_only=true" className="hover:text-white transition-colors">Eco Sustainable</Link></li>
                <li><Link to="/products?category=artwork" className="hover:text-white transition-colors">Artwork</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">AI Tools</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><Link to="/ai-visualizer" className="hover:text-white transition-colors">Room Visualiser</Link></li>
                <li><Link to="/ai-chat" className="hover:text-white transition-colors">Design Chat</Link></li>
                <li><Link to="/subscription" className="hover:text-white transition-colors">Subscription Plans</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">For Vendors</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><Link to="/auth?role=vendor" className="hover:text-white transition-colors">Become a Seller</Link></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Vendor Dashboard</Link></li>
                <li><Link to="/referral" className="hover:text-white transition-colors">Referral Programme</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/40 text-sm">
            © 2026 ARKY World. All rights reserved. www.arky.world
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
